import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useLanguage } from "../context/LanguageContext";
import { sectionPath } from "../utils/routePaths";

const ACCENT = "#C47C2B";

const categoryIcons = {
  Mountains: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
      <path d="M3 20L9 8L13 14L17 9L21 20H3Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" fill="none"/>
    </svg>
  ),
  Villages: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
      <path d="M3 20V10L8 5L13 10V20H3Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" fill="none"/>
      <path d="M13 20V13H18V20H13Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" fill="none"/>
    </svg>
  ),
  Lakes: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
      <path d="M2 12C4 10 6 14 8 12C10 10 12 14 14 12C16 10 18 14 20 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
      <path d="M2 17C4 15 6 19 8 17C10 15 12 19 14 17C16 15 18 19 20 17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
      <path d="M7 8L12 3L17 8" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" fill="none"/>
    </svg>
  ),
  Towers: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
      <rect x="8" y="3" width="8" height="18" rx="1" stroke="currentColor" strokeWidth="1.6" fill="none"/>
      <path d="M8 8H16M8 13H16" stroke="currentColor" strokeWidth="1.6"/>
      <path d="M6 21H18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
  ),
  Museums: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
      <path d="M3 21H21M3 10H21M5 10V21M19 10V21M12 3L3 10H21L12 3Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" fill="none"/>
    </svg>
  ),
  Adventures: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
      <path d="M13 4L7 14H12L11 20L17 10H12L13 4Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" fill="none"/>
    </svg>
  ),
  "Photo Spots": (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
      <rect x="2" y="7" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" fill="none"/>
      <circle cx="12" cy="14" r="3" stroke="currentColor" strokeWidth="1.6" fill="none"/>
      <path d="M9 7L10.5 4H13.5L15 7" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
    </svg>
  ),
  Culture: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
      <path d="M12 3C12 3 6 7 6 13C6 16.3 8.7 19 12 19C15.3 19 18 16.3 18 13C18 7 12 3 12 3Z" stroke="currentColor" strokeWidth="1.6" fill="none"/>
      <path d="M12 19V22M9 22H15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
  ),
  Glaciers: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
      <path d="M12 2V6M12 18V22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M2 12H6M18 12H22" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.6" fill="none"/>
    </svg>
  ),
  Churches: (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
      <path d="M12 2V6M10 4H14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
      <path d="M8 6H16V10L12 8L8 10V6Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" fill="none"/>
      <rect x="6" y="10" width="12" height="11" rx="1" stroke="currentColor" strokeWidth="1.6" fill="none"/>
      <rect x="10" y="15" width="4" height="6" stroke="currentColor" strokeWidth="1.6" fill="none"/>
    </svg>
  ),
};

const getFallbackIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" fill="none"/>
    <path d="M12 8V12L15 15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
);

export default function CategoryIconBar() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();

  useEffect(() => {
    getDocs(collection(db, "categories"))
      .then((snap) => setCategories(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="cat-grid-wrap" style={{ background: "transparent", padding: "48px 4vw 8px", display: "flex", justifyContent: "center" }}>
      <div className="cat-grid" style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 14, maxWidth: 1200, width: "100%" }}>
        {loading
          ? Array(8).fill(0).map((_, i) => (
              <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, background: "var(--bg-surface)", border: "1px solid var(--border-soft)", borderRadius: 16, padding: "24px 20px", flex: "1 1 120px", maxWidth: 150 }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--bg-surface-alt)", animation: "catShimmer 1.5s infinite" }} />
                <div style={{ width: 56, height: 10, borderRadius: 4, background: "var(--bg-surface-alt)", animation: "catShimmer 1.5s infinite" }} />
              </div>
            ))
          : categories.map((cat) => {
              const icon = cat.icon
                ? <span className="material-symbols-outlined" style={{ fontSize: 26, fontVariationSettings: "'FILL' 0" }}>{cat.icon}</span>
                : categoryIcons[cat.name] || getFallbackIcon();
              const name = language === "ka" && cat.nameGeo ? cat.nameGeo : cat.name;
              return (
                <Link key={cat.id}
                  to={sectionPath(language, "categories") + "?filter=" + encodeURIComponent(cat.name)}
                  style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, textDecoration: "none", transition: "all 0.2s", color: ACCENT, background: "var(--bg-surface)", border: "1px solid var(--border-soft)", borderRadius: 16, padding: "24px 20px", flex: "1 1 120px", maxWidth: 150, boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.1)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.04)"; e.currentTarget.style.transform = "translateY(0)"; }}>
                  <span style={{ display: "flex" }}>{icon}</span>
                  <span style={{ fontFamily: "Geist, sans-serif", fontSize: 13, fontWeight: 600, color: "var(--text-primary)", textAlign: "center", letterSpacing: "0.01em" }}>{name}</span>
                </Link>
              );
            })
        }
      </div>
      <style>{`
        @keyframes catShimmer { 0%,100%{opacity:.6} 50%{opacity:1} }
        @media (max-width: 600px) {
          .cat-grid a, .cat-grid > div { flex-basis: 90px !important; max-width: 100px !important; padding: 16px 12px !important; }
        }
  `}</style>
    </div>
  );
}
